import React, { useState } from 'react'
import { Checkbox, Pagination, Input, Button } from 'components/atoms/index';
import * as styles from 'components/organisms/Table/Table.css';
import { Icon } from '@/icons';

type ExamTicketStatus = 'AVAILABLE' | 'ASSIGNED' | 'USED' | 'EXPIRED' | 'REFUNDED';
type ExamGradingStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED';

interface StudentData {
  id: string;
  name: string;
  loginId?: string;
  email: string;
  grade: string;
  status: 'active' | 'inactive';
  registrationDate: string;
  academyCode: string;
  className?: string;
  birthDate: string;
  examTicketStatus?: ExamTicketStatus;
  examGradingStatus?: ExamGradingStatus;
  warningSubmitted?: boolean;
}

interface AcademyStudentTableProps {
  data: StudentData[];
  selectedStudents: StudentData[];
  onStudentSelect: (student: StudentData) => void;
  onSelectAll: (students: StudentData[]) => void;
  type?: 'selection' | 'display';
  displayType?: 'exam' | 'trophy' | 'learning';
  currentPage?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;
  showSearch?: boolean;
  showPagination?: boolean;
  onExamAction?: (studentId: string, action: 'recover' | 'complete') => void;
  onDelete?: (student: StudentData) => void;
  searchTerm?: string;
  onSearchChange?: (value: string) => void;
  showAcademyCode?: boolean;
  showClassName?: boolean;
  searchPlaceholder?: string;
}



export default function AcademyStudentTable({
  data,
  selectedStudents,
  onStudentSelect,
  onSelectAll,
  type = 'selection',
  displayType = 'exam',
  currentPage = 1,
  pageSize = 10,
  total = 100,
  onPageChange,
  showSearch = true,
  showPagination = true,
  onExamAction,
  onDelete,
  searchTerm: externalSearchTerm,
  onSearchChange,
  showAcademyCode = true,
  showClassName = false,
  searchPlaceholder,
}: AcademyStudentTableProps) {
    const [internalSearchTerm, setInternalSearchTerm] = useState('');
    const searchTerm = externalSearchTerm !== undefined ? externalSearchTerm : internalSearchTerm;
    const handleSearchChange = onSearchChange || setInternalSearchTerm;
    const revokeButtonClass = 'px-3 py-1 rounded-full text-[11px] font-semibold border transition-colors focus:outline-none';

    const effectiveSearchPlaceholder = searchPlaceholder ?? (
      showAcademyCode ? '학생명 또는 학원코드로 검색' : '학생명을 검색하세요'
    );

    // 서버 페이징 사용 여부 (onSearchChange가 있으면 서버 페이징)
    const useServerPaging = !!onSearchChange;

    const filteredData = showSearch && !useServerPaging
      ? data.filter(student =>
          student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          student.academyCode.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : data;

    // 서버 페이징이면 slice 안함, 클라이언트 페이징이면 slice
    const displayData = useServerPaging ? filteredData : filteredData.slice((currentPage - 1) * pageSize, (currentPage - 1) * pageSize + pageSize);

    const resolveExamStatusMeta = (student: StudentData) => {
      if (student.warningSubmitted) {
        return {
          label: '경고종료',
          className: 'text-red-600 bg-red-50 border border-red-100'
        };
      }
      if (!student.examTicketStatus) return null;

      if (student.examTicketStatus === 'ASSIGNED') {
        return {
          label: '배정완료',
          className: 'text-indigo-600 bg-indigo-50 border border-indigo-100'
        };
      }

      if (student.examTicketStatus === 'USED') {
        if (student.examGradingStatus === 'IN_PROGRESS') {
          return {
            label: '채점중',
            className: 'text-orange-600 bg-orange-50 border border-orange-200'
          };
        }
        if (student.examGradingStatus === 'COMPLETED') {
          return {
            label: '채점완료',
            className: 'text-green-600 bg-green-50 border border-green-200'
          };
        }
        return {
          label: '응시완료',
          className: 'text-slate-600 bg-slate-100 border border-slate-200'
        };
      }

      if (student.examTicketStatus === 'REFUNDED') {
        return {
          label: '환불완료',
          className: 'text-gray-500 bg-gray-100 border border-gray-200'
        };
      }

      if (student.examTicketStatus === 'EXPIRED') {
        return {
          label: '만료됨',
          className: 'text-gray-500 bg-gray-100 border border-gray-200'
        };
      }

      return null;
    };

  const handleSelectAll = () => {
    console.log('handleSelectAll 호출됨');
    // 현재 페이지에 표시된 항목 기준으로 전체 선택/해제
    const pagedData = displayData;
    const isAllSelectedOnPage = pagedData.length > 0 && pagedData.every(s => selectedStudents.some(sel => sel.id === s.id));
    console.log('전체 선택 상태:', isAllSelectedOnPage);
    
    if (isAllSelectedOnPage) {
      const newSelected = selectedStudents.filter(sel => !pagedData.some(p => p.id === sel.id));
      console.log('전체 선택 해제');
      onSelectAll(newSelected);
    } else {
      const toAdd = pagedData.filter(p => !selectedStudents.some(sel => sel.id === p.id));
      const newSelected = [...selectedStudents, ...toAdd];
      console.log('전체 선택, 추가할 학생 수:', toAdd.length);
      onSelectAll(newSelected);
    }
  };

  return (
    <section>
        {/* 검색 영역 */}
        {showSearch && (
          <div className='flex gap-2 items-center mb-3'>
            <Input
              placeholder={effectiveSearchPlaceholder}
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className='flex-1 !text-sm'
              icon={<Icon name="search" size={20}  />}
            />
          </div>
        )}
        
        <div className=''>
        <ul className={`${styles.academyHeaderList} w-full`}>
            {type === 'selection' && (
              <li className={`${styles.headerItem} max-w-[60px] justify-center !py-1 !text-xs`}>
                <div className='flex justify-center items-center' onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={(displayData.length > 0) && displayData.every(s => selectedStudents.some(sel => sel.id === s.id))}
                    onChange={(checked) => {
                      console.log('전체 선택 Checkbox onChange:', checked);
                      handleSelectAll();
                    }}
                    showIcon={false}
                    size="small"
                  />
                </div>
              </li>
            )}
            <li className={`${styles.headerItem} max-w-[80px] justify-center !py-1 !text-xs`}>순번</li>
            {showAcademyCode && (
            <li className={`${styles.headerItem} max-w-[120px] justify-center !py-1 !text-xs`}>학원코드</li>
            )}
            {showClassName && (
            <li className={`${styles.headerItem} max-w-[120px] justify-center !py-1 !text-xs`}>클래스</li>
            )}
            <li className={`${styles.headerItem} !py-1 !text-xs`}>학생명 / 학생ID</li>
            <li className={`${styles.headerItem} !py-1 !text-xs`}>생년월일</li>
            {type === 'display' && (
              <li className={`${styles.headerItem} max-w-[120px] justify-center !py-1 !text-xs`}>
                {displayType === 'exam' ? '응시권' : '액션'}
              </li>
            )}
        </ul>
        
        <div className='flex flex-col gap-0 w-full'>
            {displayData.map((student, index) => {
              const isSelected = selectedStudents.find(s => s.id === student.id) !== undefined;
              const examStatusMeta = resolveExamStatusMeta(student);
              const warningSubmission = !!student.warningSubmitted;
              const canRevokeTicket = student.examTicketStatus
                ? student.examTicketStatus === 'ASSIGNED' && !warningSubmission
                : !warningSubmission;
              
              return (
            <ul 
                key={student.id} 
                className={`${styles.academyBodyList} lg:shadow-none table-card !py-1 ${type === 'selection' ? 'cursor-pointer hover:bg-gray-50' : ''} transition-colors`}
                onClick={() => type === 'selection' && onStudentSelect(student)}
            >
                {type === 'selection' && (
                  <li className={`${styles.bodyItem} max-w-[60px] justify-center !py-1 !text-xs`}>
                    <div onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onChange={(checked) => {
                          console.log('Checkbox onChange:', student.name, checked);
                          onStudentSelect(student);
                        }}
                        showIcon={false}
                        size="small"
                      />
                    </div>
                  </li>
                )}
                <li className={`${styles.bodyItem} max-w-[80px] justify-center text-gray-500 !py-1 !text-xs`}>
                {useServerPaging ? (total - ((currentPage - 1) * pageSize + index)) : (filteredData.length - ((currentPage - 1) * pageSize + index))}
                </li>
                {showAcademyCode && (
                <li className={`${styles.bodyItem} max-w-[120px] justify-center text-gray-500 !py-1 !text-xs`}>
                {student.academyCode}
                </li>
                )}
                {showClassName && (
                <li className={`${styles.bodyItem} max-w-[120px] justify-center text-gray-500 !py-1 !text-xs`}>
                {student.className || '-'}
                </li>
                )}
                <li className={`${styles.bodyItem} font-semibold !py-1 !text-xs`}>
                  <span>{student.name}</span>
                  <span className='ml-1 font-normal text-gray-500'>
                    ({student.loginId || student.email || student.id})
                  </span>
                </li>
                <li className={`${styles.bodyItem} !py-1 !text-xs`}>
                {student.birthDate}
                </li>
                {type === 'display' && (
                  <li className={`${styles.bodyItem} max-w-[120px] justify-center !py-1 !text-xs`}>
                    {displayType === 'exam' ? (
                      <div className='flex flex-col gap-1 items-center'>
                        {examStatusMeta && (
                          <span className={`px-3 py-1 rounded-full text-[11px] font-semibold ${examStatusMeta.className}`}>
                            {examStatusMeta.label}
                          </span>
                        )}
                        {canRevokeTicket ? (
                          <button
                            type='button'
                            className={`${revokeButtonClass} text-red-600 bg-red-50 border-red-100 hover:bg-red-100`}
                            onClick={(e) => {
                              e.stopPropagation();
                              onExamAction?.(student.id, 'recover');
                            }}
                          >
                            응시권 회수
                          </button>
                        ) : (
                          <span className='text-[11px] text-gray-400'>회수 불가</span>
                        )}
                      </div>
                    ) : (
                      <Button 
                        intent='danger'
                        size='small'
                        outline
                            onClick={(e) => {
                          e.stopPropagation();
                          onDelete?.(student);
                        }}
                      >
                        삭제
                      </Button>
                    )}
                  </li>
                )}
            </ul>
              );
            })}
        </div>
        
        {showPagination && (
          <div className='mt-3'>
            <Pagination
              total={useServerPaging ? total : filteredData.length}
              pageSize={pageSize}
              current={currentPage}
              onChange={(page) => {
                onPageChange?.(page);
              }}
            />
          </div>
        )}
        </div>
    </section>
  )
}